import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import { emit } from '../../platform/analytics/analytics'
import { daysBetween, isOverdue, type AmountChange, type LocalDate } from './recurringRules'
import type { Bill } from './recurringWire'
import type { MarkPaidChoice } from './useMarkPaid'

/**
 * M7's analytics (Task 16): every recurring event leaves through here, typed, so
 * a call site cannot hand `emit` a bill's name, merchant or amount. Rule 7: counts,
 * durations, enums and booleans only. `vocabulary.ts` refuses anything else at
 * runtime; these signatures refuse it at compile time first.
 *
 * Renamed from PRD-M7's table where its key could not pass the allow-list (the
 * table records each): `was_overdue` → `overdue_shown`, `days_late` →
 * `late_days`, `count` → `suggestion_count`. `recurring_viewed` once carried a
 * `view_mode` (List | Calendar); #704 made the month one screen, so the
 * property and `recurring_view_changed` were retired (Ruling 58).
 */

/** How a bill came to exist. `suggestion` is an accepted detection. */
export type CreatedSource = 'manual' | 'from_transaction' | 'suggestion'

export function emitViewed(p: { activeCount: number; overdueCount: number | undefined }): void {
  emit('recurring_viewed', { active_count: p.activeCount, overdue_count: p.overdueCount })
}

/**
 * `recurring_viewed` once per mount of the month screen; changing month is not
 * a second view.
 *
 * It waits for two things, because the ref lets only ONE emit through and an
 * early one cannot be corrected:
 *
 * - the screen's data (`activeCount`);
 * - the badge's overdue count (`useRecurringOverdueCount`), unless its read
 *   FAILED: then the view goes out without it, which the vocabulary records as
 *   "not measured", never 0. Bounded either way — the read settles.
 */
export function useRecurringViewed(p: {
  activeCount: number | undefined
  overdue: { count: number | undefined; failed: boolean }
}): void {
  const sent = useRef(false)
  const { activeCount } = p
  const { count: overdueCount, failed } = p.overdue
  useEffect(() => {
    if (sent.current || activeCount === undefined) return
    if (overdueCount === undefined && !failed) return
    sent.current = true
    emitViewed({ activeCount, overdueCount })
  }, [activeCount, overdueCount, failed])
}

/**
 * What a mark-paid says about the period it paid: the period the choice names
 * (a skipped one, #826), else `bill.nextDue`, the one the server pays (Ruling 34).
 *
 * - `overdue_shown`: the row stood in the badge's count when it was paid — an
 *   OUTFLOW overdue by P3 (`isOverdue`, local `today`). An inflow is income,
 *   never overdue on the badge (D6), so it is never `true` here either. A
 *   skipped period is in Skipped, not Overdue, so a named period is `false`.
 * - `late_days`: whole calendar days from the due date to the date it was PAID —
 *   the request's own `paidDate` (a link's transaction date, cash's local today),
 *   not the moment the server answered. `daysBetween`, DST-proof; never negative,
 *   so paying early is 0 days late; 0 when the bill has no next due date.
 * - `transaction_present`: linked to a transaction, rather than paid as cash.
 */
export function markPaidProps(
  bill: Bill,
  choice: MarkPaidChoice,
  today: LocalDate,
): { overdue_shown: boolean; late_days: number; transaction_present: boolean } {
  const period = choice.transactionId === null ? (choice.period ?? null) : null
  const due = period ?? bill.nextDue
  return {
    overdue_shown: period === null && due !== null && !bill.isInflow && isOverdue(bill, { dueDate: due, status: 'upcoming' }, today),
    late_days: due === null ? 0 : Math.max(0, daysBetween(due, choice.paidDate)),
    transaction_present: choice.transactionId !== null,
  }
}

export function emitMarkedPaid(bill: Bill, choice: MarkPaidChoice, today: LocalDate): void {
  emit('recurring_marked_paid', markPaidProps(bill, choice, today))
}

export type LifecycleVerb = 'skip' | 'pause' | 'resume' | 'delete'

const LIFECYCLE_EVENT = {
  skip: 'recurring_skipped',
  pause: 'recurring_paused',
  resume: 'recurring_resumed',
  delete: 'recurring_deleted',
} as const

export function emitLifecycle(verb: LifecycleVerb): void {
  emit(LIFECYCLE_EVENT[verb])
}

export function emitCreated(source: CreatedSource): void {
  emit('recurring_created', { source })
}

export function emitEdited(): void {
  emit('recurring_edited')
}

/** An accepted suggestion is a creation too: the funnel event and the creation series, once each. */
export function emitSuggestionAccepted(): void {
  emit('recurring_suggestion_accepted')
  emitCreated('suggestion')
}

export function emitSuggestionDismissed(): void {
  emit('recurring_suggestion_dismissed')
}

/** `recurring_suggestion_shown` once per mount, on the first non-empty list. */
export function useSuggestionsShown(count: number | undefined): void {
  const sent = useRef(false)
  useEffect(() => {
    if (sent.current || count === undefined || count === 0) return
    sent.current = true
    emit('recurring_suggestion_shown', { suggestion_count: count })
  }, [count])
}

/** A grid day narrowed the list (#704); clearing is not an event. The count is every entry on the day, inflows included. */
export function emitDayOpened(itemCount: number): void {
  emit('calendar_day_opened', { item_count: itemCount })
}

export type LinkOutcome = { op: 'link' } | { op: 'unlink' } | { op: 'find'; linked: number }

export function emitLinkOutcome(out: LinkOutcome): void {
  if (out.op === 'link') emit('recurring_payment_linked')
  else if (out.op === 'unlink') emit('recurring_payment_unlinked')
  else emit('recurring_match_history', { linked_count: out.linked })
}

/**
 * "Once per bill per mount" for `recurring_amount_change_shown`. A screen that
 * draws amount-change chips provides one scope; every chip in it reports through
 * `useAmountChangeShown`, and the scope's set remembers which bills have already
 * been counted. A weekly bill with four rows in a month is one event, and a
 * re-render, a month change or a day narrowing that redraws the chip is none.
 *
 * **The scope is required.** A chip outside one emits nothing: a per-chip
 * fallback would over-count silently (every remount a new "showing"), and a
 * silent over-count is the failure nobody notices. Every screen that draws
 * `Chips` — the month list (narrowed or not) and All recurring — is
 * pinned to emit through its scope by a test, so a new unscoped caller shows up
 * as an event that never arrives.
 */
const ShownScope = createContext<Set<string> | null>(null)

export function AmountChangeShownScope({ children }: { children: ReactNode }) {
  const [seen] = useState(() => new Set<string>())
  return <ShownScope.Provider value={seen}>{children}</ShownScope.Provider>
}

export function useAmountChangeShown(billId: string, change: AmountChange | null | undefined): void {
  const scope = useContext(ShownScope)
  const direction = change?.direction
  useEffect(() => {
    if (scope === null || direction === undefined || scope.has(billId)) return
    scope.add(billId)
    emit('recurring_amount_change_shown', { direction })
  }, [scope, billId, direction])
}
